import { MS_PER_MINUTE } from "@repo/api";
import type { ActionState } from "@/shared/common/action-button";

export type { ActionState };

export interface AppointmentActionStates {
  checkIn: ActionState;
  admit: ActionState;
  noShow: ActionState;
  cancel: ActionState;
  complete: ActionState;
  reschedule: ActionState;
  /** True when the visit is in progress and now > end */
  isOverdue: boolean;
  /** Minutes past appointment end (0 if not overdue) */
  overdueMinutes: number;
}

interface VisitTimeShape {
  start?: string;
  end?: string;
  status?: string;
}

/** Compute client-side action availability for a visit. Mirrors server rules. */
export function getAppointmentActionStates(appointment: VisitTimeShape | null, now: Date): AppointmentActionStates {
  const disallowed: AppointmentActionStates = {
    checkIn: { allowed: false },
    admit: { allowed: false },
    noShow: { allowed: false },
    cancel: { allowed: false },
    complete: { allowed: false },
    reschedule: { allowed: false },
    isOverdue: false,
    overdueMinutes: 0,
  };

  if (!appointment?.start || !appointment?.end) return disallowed;

  const start = new Date(appointment.start);
  const end = new Date(appointment.end);

  // Check-in: any time until end
  const checkIn: ActionState = now > end ? { allowed: false, reason: "Appointment has ended" } : { allowed: true };

  // Admit: no time constraint. The window governs who may knock, not how late
  // the clinician runs — refusing a late admission strands a waiting patient.
  const admit: ActionState = { allowed: true };

  // No-show: only after start
  const noShow: ActionState =
    now < start ? { allowed: false, reason: "Cannot mark no-show before appointment start" } : { allowed: true };

  // Cancel: no time constraint (state machine handles terminal states)
  const cancel: ActionState = { allowed: true };

  // Complete: no time constraint
  const complete: ActionState = { allowed: true };

  // Reschedule: only before start
  const reschedule: ActionState =
    now >= start ? { allowed: false, reason: "Cannot reschedule a started or past appointment" } : { allowed: true };

  // Overdue: in progress and past end time
  const isOverdue = appointment.status === "inProgress" && now > end;
  const overdueMinutes = isOverdue ? Math.floor((now.getTime() - end.getTime()) / MS_PER_MINUTE) : 0;

  return { checkIn, admit, noShow, cancel, complete, reschedule, isOverdue, overdueMinutes };
}
